<template>
  <div v-if="showModal" class="modal-container">
    <div
      class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40"
    ></div>
    <div class="z-50 w-[550px] relative">
      <button
        @click="$emit('close')"
        class="absolute -top-12 right-0 px-1 py-1 bg-white rounded-full shadow-md hover:bg-gray-100"
      >
        <img
          src="@/assets/tasks/iconoir_cancel.svg"
          alt="Close"
          class="h-6"
        />
      </button>

      <div
        class="modal-content relative bg-white w-full max-h-[90vh] flex-col flex rounded-[12px] shadow-lg"
      >
        <div class="w-full border-b-[1px] mb-4 px-6 py-4">
          <h2 class="text-[18px] text-grayDark font-bold">
            Assign Template: {{ template?.topic }}
          </h2>
        </div>

        <form @submit.prevent="handleSubmit" class="overflow-y-auto px-8">
          <div class="space-y-4 mb-4">
            
            <div class="flex flex-col gap-2">
              <label class="block text-[13px] font-medium text-gray-700">
                Select Team Members
              </label>
              <CustomSelect
                :options="formattedMembers"
                v-model="selectedTalents"
                labelKey="displayName"
                valueKey="talent_id"
                placeholder="Search and select team members"
                multiple
                :disabled="loading"
              />
              <div v-if="loading" class="text-blue-500 text-sm mt-1">
                Loading team members...
              </div>
              <div v-if="members.length === 0 && !loading" class="text-yellow-600 text-sm mt-1">
                This team has no members to assign the template to.
              </div>
            </div>

          </div>

          <div class="flex mb-4 mt-6 text-[14px]">
            <button
              type="submit"
              class="gap-2 py-2 px-4 bg-primaryDarkBg w-auto min-w-full h-[42px] text-white rounded disabled:opacity-50"
              :disabled="isSubmitting || selectedTalents.length === 0"
            >
              {{ isSubmitting ? "Assigning..." : "Assign to Selected Members" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/axios";
import CustomSelect from "../team/CustomSelect.vue";

export default {
  name: "AssignTemplateModal",
  components: {
    CustomSelect,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    teamId: {
      type: [String, Number],
      required: true,
    },
    template: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      isSubmitting: false,
      loading: false,
      members: [],
      selectedTalents: [],
    };
  },
  computed: {
    formattedMembers() {
      return this.members.map((member) => {
        const displayName = member.member_name || "Unnamed";

        return {
          ...member,
          talent_id: member.talent_id,
          displayName
        };
      });
    }
  },
  watch: {
    showModal(newValue) {
      if (newValue && this.teamId) {
        this.resetForm();
        this.fetchTeamMembers();
      }
    }
  },
  methods: {
    resetForm() {
      this.selectedTalents = [];
      this.isSubmitting = false;
      this.members = [];
    },
    async fetchTeamMembers() {
      try {
        this.loading = true;
        const response = await apiClient.get(`/web/teams/${this.teamId}`);
        if (response.data && response.data.data) {
           this.members = response.data.data.members || [];
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
        this.$toast.error("Failed to load team members");
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit() {
      try {
        this.isSubmitting = true;
        const assignmentData = {
          talent_ids: this.selectedTalents
        };
        
        await apiClient.post(
          `/web/teams/${this.teamId}/templates/${this.template.id}/assign`, 
          assignmentData,
          { timeout: 60000 } // Increase timeout to 60s for copying large templates
        );
        
        if (this.$toast) {
          this.$toast.success("Learning plan assigned successfully to selected members!");
        } else {
          alert("Learning plan assigned successfully to selected members!");
        }
        this.$emit("template-assigned");
        this.$emit("close");
      } catch (error) {
        console.error("Error assigning template:", error);
        
        let errorMsg = "Failed to assign learning plan";
        if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.message) {
          errorMsg = error.message;
        }

        if (this.$toast) {
          this.$toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 50;
}

.modal-content {
  position: relative;
  z-index: 60;
}
</style>
